/**
 * Backup Service
 * Collects all localStorage data and creates comprehensive backups
 */

import { STORAGE_KEYS as SHARED_KEYS } from 'constants/storageKeys';
import { persistence } from 'services/persistence';
import { hashSHA256 } from 'utils/crypto';

export interface CompleteBackup {
  version: string;
  appVersion: string;
  syncedAt: string;
  deviceId: string;
  checksum: string;
  data: {
    clinicalData: Record<string, any>;
    chatAndAI: Record<string, any>;
    healthTracking: Record<string, any>;
    personalGrowth: Record<string, any>;
    calendar: Record<string, any>;
    userInterface: Record<string, any>;
  };
}

// All localStorage keys organized by category
const STORAGE_KEYS = {
  clinicalData: [
    'eir-data',
    'eir-file-content',
    'eir-journal-notes',
    'eir-health-analysis',
    'eir-health-analysis-meta',
  ],
  chatAndAI: [
    'eir-chat-messages',
    'eir-chat-journal',
    SHARED_KEYS.CHAT_THREADS,
    'ai-assessment',
    SHARED_KEYS.HEALTH_MEMORY_ITEMS,
    SHARED_KEYS.HEALTH_MEMORY_ENCRYPTION_KEY,
  ],
  healthTracking: [
    SHARED_KEYS.HEALTH_ACTIONS,
    SHARED_KEYS.HEALTH_ASSESSMENT_RESULTS,
    SHARED_KEYS.HEALTH_ASSESSMENT_RESPONSES,
    'eir-nutrition-meal-log',
    'eir-hydration-log',
    'eir-activity-log',
    'eir-sleep-log',
    'eir-bowel-movement-log',
    'movement-log',
    'water-intake-log',
    'sleep-tracking-log',
  ],
  personalGrowth: [
    'user-created-cbt-programs',
    'cbt-program-responses',
    'cbt-program-completed',
    'cbt-active-program',
    'personal-action-plans',
    'personal-goals',
    'room-for-improvement',
    'habit-tracker-data',
    'eir-writing-journal',
    'eir-app-feedback',
    'word-harmony-state',
    'feedback-data',
    'weekly-challenges-progress',
  ],
  calendar: [
    'eir-calendar-events',
    'eir-calendar-suggestions',
  ],
  userInterface: [
    'eir-for-you-items',
    'eir-user-profile',
    'page-history',
    'onboarding-completed',
    'cbt-module-completion-message',
  ],
};

// Keys to exclude from backup (auth tokens, temporary data)
const EXCLUDED_KEYS = [
  'supabase-session',
  'google-calendar-token',
  'subscription-cache',
  'google-drive-token',
  'google-drive-token-expiry',
];

/**
 * Get or create device ID
 */
function getDeviceId(): string {
  const key = 'device-id';
  let deviceId = persistence.getItem(key);
  if (!deviceId) {
    deviceId = `device-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    persistence.setItem(key, deviceId);
  }
  return deviceId;
}

/**
 * Collect all localStorage data organized by category
 */
export function collectAllLocalStorageData(): CompleteBackup['data'] {
  const data: CompleteBackup['data'] = {
    clinicalData: {},
    chatAndAI: {},
    healthTracking: {},
    personalGrowth: {},
    calendar: {},
    userInterface: {},
  };

  // Collect data for each category
  Object.entries(STORAGE_KEYS).forEach(([category, keys]) => {
    keys.forEach((key) => {
      try {
        const value = persistence.getItem(key);
        if (value !== null) {
          // Try to parse as JSON, otherwise store as string
          try {
            data[category as keyof typeof data][key] = JSON.parse(value);
          } catch {
            data[category as keyof typeof data][key] = value;
          }
        }
      } catch (error) {
        console.warn(`[BackupService] Failed to collect data for key: ${key}`, error);
      }
    });
  });

  return data;
}

/**
 * Create a complete backup snapshot
 */
export async function createBackupSnapshot(): Promise<CompleteBackup> {
  const data = collectAllLocalStorageData();
  const dataString = JSON.stringify(data);
  const checksum = await hashSHA256(dataString);

  const backup: CompleteBackup = {
    version: '1.0',
    appVersion: '0.1.0',
    syncedAt: new Date().toISOString(),
    deviceId: getDeviceId(),
    checksum,
    data,
  };

  return backup;
}

/**
 * Restore all localStorage data from backup
 */
export function restoreAllLocalStorageData(backup: CompleteBackup): void {
  if (!backup || !backup.data) {
    throw new Error('Invalid backup data');
  }

  console.log('[BackupService] Restoring data from backup...');
  let restoredCount = 0;

  // Restore data for each category
  Object.values(backup.data).forEach((categoryData) => {
    Object.entries(categoryData).forEach(([key, value]) => {
      try {
        if (EXCLUDED_KEYS.includes(key)) {
          return; // Skip excluded keys
        }
        const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
        persistence.setItem(key, stringValue);
        restoredCount++;
      } catch (error) {
        console.warn(`[BackupService] Failed to restore key: ${key}`, error);
      }
    });
  });

  console.log(`[BackupService] Restored ${restoredCount} items from backup`);
}

/**
 * Validate backup integrity
 */
export async function validateBackup(backup: CompleteBackup): Promise<boolean> {
  if (!backup || typeof backup !== 'object') {
    return false;
  }

  if (!backup.version || !backup.data || !backup.syncedAt) {
    return false;
  }

  // Verify checksum if available
  if (backup.checksum && backup.checksum !== 'no-checksum') {
    const dataString = JSON.stringify(backup.data);
    const calculatedChecksum = await hashSHA256(dataString);
    if (calculatedChecksum !== backup.checksum) {
      console.warn('[BackupService] Checksum mismatch - data may be corrupted');
      return false;
    }
  }

  return true;
}

/**
 * Merge two backups intelligently
 * Strategy: Last-write-wins with array merging by ID
 */
export function mergeBackups(localBackup: CompleteBackup, cloudBackup: CompleteBackup): CompleteBackup {
  console.log('[BackupService] Merging backups...');
  console.log('Local sync time:', localBackup.syncedAt);
  console.log('Cloud sync time:', cloudBackup.syncedAt);

  // If cloud is newer, prefer cloud data
  const localTime = new Date(localBackup.syncedAt).getTime();
  const cloudTime = new Date(cloudBackup.syncedAt).getTime();

  if (cloudTime > localTime) {
    console.log('[BackupService] Cloud backup is newer, using cloud data');
    return cloudBackup;
  } else if (localTime > cloudTime) {
    console.log('[BackupService] Local backup is newer, using local data');
    return localBackup;
  }

  // If timestamps are the same, merge intelligently
  console.log('[BackupService] Timestamps equal, performing intelligent merge');
  const mergedData: CompleteBackup['data'] = {
    clinicalData: {},
    chatAndAI: {},
    healthTracking: {},
    personalGrowth: {},
    calendar: {},
    userInterface: {},
  };

  // Merge each category
  const categories = Object.keys(mergedData) as Array<keyof CompleteBackup['data']>;
  categories.forEach((category) => {
    const localCat = localBackup.data[category] || {};
    const cloudCat = cloudBackup.data[category] || {};
    const allKeys = new Set([...Object.keys(localCat), ...Object.keys(cloudCat)]);

    allKeys.forEach((key) => {
      const localValue = localCat[key];
      const cloudValue = cloudCat[key];

      // If only one has the value, use it
      if (localValue === undefined) {
        mergedData[category][key] = cloudValue;
      } else if (cloudValue === undefined) {
        mergedData[category][key] = localValue;
      } else if (Array.isArray(localValue) && Array.isArray(cloudValue)) {
        // Merge arrays by ID, keeping unique items
        mergedData[category][key] = mergeArrays(localValue, cloudValue);
      } else {
        // For objects/primitives, prefer cloud (assuming it's more recent)
        mergedData[category][key] = cloudValue;
      }
    });
  });

  return {
    ...cloudBackup,
    syncedAt: new Date().toISOString(),
    data: mergedData,
  };
}

/**
 * Merge two arrays intelligently, keeping unique items by ID
 */
function mergeArrays(localArray: any[], cloudArray: any[]): any[] {
  const merged = new Map();

  // Add all local items
  localArray.forEach((item) => {
    const id = item.id || JSON.stringify(item);
    merged.set(id, item);
  });

  // Add/update with cloud items
  cloudArray.forEach((item) => {
    const id = item.id || JSON.stringify(item);
    const existing = merged.get(id);
    
    if (!existing) {
      merged.set(id, item);
    } else if (item.addedAt || item.timestamp || item.date) {
      // If items have timestamps, keep the newer one
      const existingTime = new Date(existing.addedAt || existing.timestamp || existing.date || 0).getTime();
      const itemTime = new Date(item.addedAt || item.timestamp || item.date || 0).getTime();
      
      if (itemTime > existingTime) {
        merged.set(id, item);
      }
    }
  });

  return Array.from(merged.values());
}

/**
 * Get backup statistics
 */
export function getBackupStats(backup: CompleteBackup): {
  totalItems: number;
  categories: Record<string, number>;
  sizeEstimate: string;
} {
  let totalItems = 0;
  const categories: Record<string, number> = {};

  Object.entries(backup.data).forEach(([category, categoryData]) => {
    const count = Object.keys(categoryData).length;
    categories[category] = count;
    totalItems += count;
  });

  const jsonString = JSON.stringify(backup);
  const sizeInBytes = new Blob([jsonString]).size;
  const sizeInKB = (sizeInBytes / 1024).toFixed(2);
  const sizeInMB = (sizeInBytes / (1024 * 1024)).toFixed(2);
  const sizeEstimate = sizeInBytes > 1024 * 1024 ? `${sizeInMB} MB` : `${sizeInKB} KB`;

  return {
    totalItems,
    categories,
    sizeEstimate,
  };
}

export const backupService = {
  createBackupSnapshot,
  collectAllLocalStorageData,
  restoreAllLocalStorageData,
  validateBackup,
  mergeBackups,
  getBackupStats,
};
